import { ReverseMortgageLoanType, ReverseMortgageProgram, ReverseMortgageProgramType } from '@lenderful/domain';
import { isReverseMortgageType } from 'app/api/loan-application/universal/mappings';
import { hasMortgageOnProperty, isUsingReverseToPurchase } from 'app/models/fields/conditionals';
import { FieldNames } from 'app/models/fields/names';

/**
 * Returns estimated home value for Reverse Mortgage product.
 * 
 * @param form 
 * @returns {number}
 */
export const getEstimatedHomeValue = (form): number => {
  return isUsingReverseToPurchase(form) ? form[FieldNames.purchasePrice] : form[FieldNames.homeValue];
};

/**
 * Returns morgage balance for Reverse Mortgage product (when applicable).
 * 
 * @param form 
 * @returns {number}
 */
export const getMortgageBalance = (form): number => {
  return hasMortgageOnProperty(form) && !isUsingReverseToPurchase(form) ? form[FieldNames.mortgageBalance] : 0; 
};

/**
 * Returns line of credit for Reverse Mortgage product (when applicable).
 * 
 * @param form 
 * @returns {number | undefined}
 */
export const getLineOfCredit = (form): number | undefined => {
  return isReverseMortgageType(form, ReverseMortgageLoanType.LINE_OF_CREDIT) ? Math.max(0, form[FieldNames.netPrincipalLimit] - form[FieldNames.expectedLumpSum]) : undefined;
};

/**
 * Returns loan term for Reverse Mortgage product (when applicable).
 * 
 * @param form 
 * @returns {number | undefined}
 */
export const getLoanTerm = (form): number | undefined => {
  return isReverseMortgageType(form, ReverseMortgageLoanType.MONTHLY_PAYMENTS) ? form[FieldNames.loanTerm] : undefined;
};

/**
 * Returns tenure payment for Reverse Mortgage product (when applicable).
 * 
 * @param form 
 * @returns {number | undefined}
 */
export const getTenurePayment = (form): number | undefined => {
  return isReverseMortgageType(form, ReverseMortgageLoanType.MONTHLY_PAYMENTS) ? form[FieldNames.tenurePayment] : undefined;
};

/**
 * Returns term payment for Reverse Mortgage product (when applicable).
 * 
 * @param form 
 * @returns {number | undefined}
 */
export const getTermPayment = (form): number | undefined => {
  return isReverseMortgageType(form, ReverseMortgageLoanType.MONTHLY_PAYMENTS) ? form[FieldNames.termPayment] : undefined;
};

/**
 * Returns down payment payment for Reverse Mortgage product.
 * 
 * @param {number} purchasePrice 
 * @param {number} netPrincipalLimit
 * @param {number} totalUpfrontCosts
 * @returns {number}
 */
export const getDownPayment = (purchasePrice: number, netPrincipalLimit: number, totalUpfrontCosts: number): number => {
  return purchasePrice - netPrincipalLimit + totalUpfrontCosts;
};

/**
 * Whether the borrower chose to apply for the EquitySelect program on the combined summary.
 * 
 * @param form 
 * @returns {boolean}
 */
export const isEquitySelectProgram = (form): boolean => {
  return form[FieldNames.reverseMortgageProgramType] === ReverseMortgageProgramType.EQUITYSELECT;
};

/**
 * Returns the program the borrower chose to apply for, with the High Tech calc figures (High Tech
 * is the calc behind EquitySelect) when that
 * is the choice. Undefined when no choice was recorded (clients without the combined summary),
 * which the backend treats as HECM.
 * 
 * @param form 
 * @returns {ReverseMortgageProgram | undefined}
 */
export const getReverseMortgageProgram = (form): ReverseMortgageProgram | undefined => {
  if (isEquitySelectProgram(form)) {
    return {
      cappedPayment       : form[FieldNames.cappedPayment],
      downPaymentRequired : form[FieldNames.highTechDownDollar],
      firstMinimumPayment : form[FieldNames.firstMinimumPayment],
      ltv                 : form[FieldNames.ltv],
      maxLineOfCredit     : form[FieldNames.maxLineOfCredit],
      maxLoanAmount       : form[FieldNames.maxLoanAmount],
      minLoanAmount       : form[FieldNames.minLoanAmount],
      paymentOption       : form[FieldNames.paymentOption],
      type                : ReverseMortgageProgramType.EQUITYSELECT,
    };
  }
  if (form[FieldNames.reverseMortgageProgramType] === ReverseMortgageProgramType.HECM) {
    return { type: ReverseMortgageProgramType.HECM };
  }
  return undefined;
};
